import React from 'react'
import { IconVolume3, IconVolume } from '@tabler/icons-react'
import { truncate } from 'lodash'

import { Toggle } from '@/components/forms/Checkbox'
import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { shortenMultipleSelectedHover } from '@/views/RealtimeCoaching/helpers'
import { formatTime } from '@/utils/helpers'

export const createAudibleAndAlertNameColumns = (alertConfig, isReadOnly, handleAudibleClick) => {
  const { audible: isAudible, id } = alertConfig
  const name = truncate(alertConfig.name, { length: 21 })

  return {
    value: alertConfig.name,
    as: (
      <div className="flex-align-center small-gap">
        <Button
          basic
          small
          square
          data-testid={`${isAudible ? 'audible-on' : 'audible-off'}`}
          disabled={isReadOnly}
          tooltip={`${isAudible ? 'Disable' : 'Enable'} audible notifications`}
          tooltipProps={{ placement: 'top-start' }}
          onClick={() => !isReadOnly && handleAudibleClick(id)}
        >
          {isAudible ? <IconVolume /> : <IconVolume3 className="status-light-muted" />}
        </Button>
        <InteractiveTooltip
          interactive
          placement="top-start"
          content={alertConfig.name}
          hide={alertConfig.name.length <= 21}
        >
          <div>{name}</div>
        </InteractiveTooltip>
      </div>
    ),
  }
}

export const createActiveColumn = (alertConfig, isReadOnly, updateActiveState) => {
  const userOrTagsSet = alertConfig.users.length > 0 || alertConfig.tags.length > 0
  const isDisabled = isReadOnly || !userOrTagsSet

  return {
    value: alertConfig.active,
    as: (
      <InteractiveTooltip hide={userOrTagsSet} content="Users or Tags required to activate">
        <Toggle
          aria-label={`Status: ${alertConfig.name}`}
          checked={alertConfig.active}
          data-testid={`is-active-check-${alertConfig.id}`}
          onClick={() => !isReadOnly && updateActiveState(alertConfig.id)}
          disabled={isDisabled}
        />
      </InteractiveTooltip>
    ),
  }
}

export const createUserColumn = (alertConfig, orgTags, orgUsers) => {
  const alertTags = alertConfig.tags
  const alertUsers = alertConfig.users
  const tagNames = orgTags.filter(({ id }) => alertTags.includes(id)).map(({ name }) => name)
  const userNames = orgUsers
    .filter(({ id }) => alertUsers.includes(id))
    .map(({ first_name, last_name }) => `${first_name} ${last_name}`)

  if (tagNames.length) {
    return shortenMultipleSelectedHover(tagNames, 'tags')
  }

  if (userNames.length) {
    return shortenMultipleSelectedHover(userNames, 'agents')
  }

  // This will happen if the users/tags are deleted
  return { value: '', as: 'Missing Agents or Tags' }
}

export const createPlaybookColumn = (alertConfig, configPaths) => {
  const cids = alertConfig.trigger_on.map(({ cid }) => cid)
  const uniqueCids = [...new Set(cids)]

  return shortenMultipleSelectedHover(
    uniqueCids.map((cid) => {
      return configPaths?.[cid]?.name || 'No Playbook Found'
    })
  )
}

export const createCreatedAtColumn = (alertConfig) => {
  return {
    value: alertConfig.created_at,
    as: (
      <InteractiveTooltip content={`Last modified ${formatTime(alertConfig.modified_at)}`}>
        {formatTime(alertConfig.created_at)}
      </InteractiveTooltip>
    ),
  }
}

export const createAlertTableRows = (
  alertConfigs = [],
  orgTags = [],
  orgUsers = [],
  configPaths = {},
  isReadOnly,
  handleAudibleClick,
  updateActiveState
) => {
  return alertConfigs.map((alertConfig) => {
    return {
      ...alertConfig,
      name: createAudibleAndAlertNameColumns(alertConfig, isReadOnly, handleAudibleClick),
      active_for: createActiveColumn(alertConfig, isReadOnly, updateActiveState),
      created_at: createCreatedAtColumn(alertConfig),
      alerts_for: createUserColumn(alertConfig, orgTags, orgUsers),
      playbooks_for: createPlaybookColumn(alertConfig, configPaths),
      // TODO: this should change once we have an actual value for created by
      created_by: alertConfig.manager_name,
    }
  })
}
